/**
 * UI Kit 1 markup components — not removed as still in use in the following:
 *
 * EmFn, StrikeFn, StringFn, StrongFn:
 *   - Used by platform/packages/forge/forge-ui/src/renderer/defaultComponents.tsx
 *     as part of the RendererNext component set.
 *
 */
import React, { type ReactNode } from 'react';
import { type Props } from '../../';
import { type MarkupProps, type StringProps } from '@atlassian/forge-ui-types';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled â€“ go/akcss
import { Text } from '@atlaskit/primitives/compiled';
import { RendererContext } from '../../../context';
import warnOnce from '../../../utils/warn-once';

const useDeprecationWarning = (componentName: string) => {
	const { forgeEnvironment } = React.useContext(RendererContext);

	React.useEffect(() => {
		if (forgeEnvironment !== 'PRODUCTION') {
			warnOnce(
				`[@forge/react: ${componentName}] Warning: The "${componentName}" component is deprecated and will be removed on the 28th of August 2025. Please use the "Text" component instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/text/.`,
			);
		}
	}, [forgeEnvironment, componentName]);
};

// taken from https://hello.atlassian.net/wiki/spaces/PRODSEC/pages/158006558/URI+scheme+Allow+listing
const PROTOCOL_ALLOWLIST = [
	'http:',
	'https:',
	'ftp:',
	'ftps:',
	'mailto:',
	'skype:',
	'callto:',
	'facetime:',
	'git:',
	'irc:',
	'irc6:',
	'news:',
	'nntp:',
	'feed:',
	'cvs:',
	'svn:',
	'mvn:',
	'ssh:',
	'scp:',
	'sftp:',
	'itms:',
	'notes:',
	'hipchat:',
	'sourcetree:',
	'urn:',
	'tel:',
	'xmpp:',
	'telnet:',
	'vnc:',
	'rdp:',
	'whatsapp:',
	'slack:',
	'sip:',
	'sips:',
	'magnet:',
	'tds:',
];

export const sanitizeUrl = (urlString?: string): string => {
	const url = new URL(urlString || '', window.location.href);
	if (PROTOCOL_ALLOWLIST.includes(url.protocol)) {
		return url.toString();
	} else {
		return 'about:blank';
	}
};

// this is different to Rendered<T>, as ReactNode supports inline strings
type ReactChildren = { children: ReactNode | ReactNode[] };
type Reacted<T> = Omit<T, 'children'> & ReactChildren;

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
/**
 * @deprecated The "Em" component is deprecated and will be removed on the 28th of August 2025. Please use the "Text" component instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/text/.
 */
export function Em({ children }: Reacted<MarkupProps>): React.JSX.Element {
	useDeprecationWarning('Em');
	return <Text as="em">{children}</Text>;
}

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
/**
 * @deprecated The "Strike" component is deprecated and will be removed on the 28th of August 2025. Please use the "Text" component instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/text/.
 */
export function Strike({ children }: Reacted<MarkupProps>): React.JSX.Element {
	useDeprecationWarning('Strike');
	return <s>{children}</s>;
}

export function String({ text }: Reacted<StringProps>): React.JSX.Element {
	return <>{text}</>;
}

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
/**
 * @deprecated The "Strong" component is deprecated and will be removed on the 28th of August 2025. Please use the "Text" component instead. For more information, visit: https://developer.atlassian.com/platform/forge/ui-kit/components/text/.
 */
export function Strong({ children }: Reacted<MarkupProps>): React.JSX.Element {
	useDeprecationWarning('Strong');
	return <Text as="strong">{children}</Text>;
}

function wrap<T extends object>(Component: (_: Reacted<T>) => JSX.Element) {
	return function WrappedComponent({
		Components,
		children,
		props: propsAny,
		render,
		renderChildren,
		dispatch,
	}: Props): React.JSX.Element {
		const props = propsAny as Omit<T, 'children'>;

		return (
			<Component {...props}>{renderChildren({ Components, children, render, dispatch })}</Component>
		);
	};
}

export const EmFn = wrap<MarkupProps>(Em);
export const StrikeFn = wrap<MarkupProps>(Strike);
export const StringFn = wrap<StringProps>(String);
export const StrongFn = wrap<MarkupProps>(Strong);
